import { ComponentPropsWithoutRef } from 'react';
import * as CFHelpers from 'components/custom-fields/helpers';
import { EmptyData } from 'components/util';
import { useCustomFieldUrl } from 'customHooks/useCustomFileUrl';
import { formatUploadedFileName } from 'helpers/formatting';
import { CustomField, CustomFieldResponse } from 'types/domain';

interface CustomFieldDisplayValueProps<T extends CustomField>
    extends Omit<ComponentPropsWithoutRef<'p'>, 'children'> {
    field: T;
    response?: CustomFieldResponse<T['type']>;
}

const CustomFieldDisplayValue = <T extends CustomField>(props: CustomFieldDisplayValueProps<T>) => {
    const { className = '', field, response: responseConsumer, ...rest } = props;
    const response = responseConsumer ?? field.response;

    const fileUrl = useCustomFieldUrl(response?.id);

    const hasNoResponse =
        typeof response === 'undefined' ||
        response === null ||
        response.value === null ||
        (Array.isArray(response?.value) && response.value.length === 0);

    if (!field.default_value && hasNoResponse) {
        return <EmptyData className={className} />;
    }

    if (field.type === 'file') {
        return (
            <a href={fileUrl} target="_blank" rel="noopener noreferrer">
                {formatUploadedFileName(response.value_friendly)}
            </a>
        );
    }

    return (
        <p className={className} {...rest}>
            {CFHelpers.getReadableDisplayValueByType(field)}
        </p>
    );
};

const DisplayValue = CustomFieldDisplayValue;

export {
    CustomFieldDisplayValue,
    //
    DisplayValue,
};
